import React, { createContext, useContext, useEffect, useState } from "react";

/**
 * WorkspaceContext — global currency + region selector state.
 *
 * Currency rules (REX-001, Feb 2026):
 *   - Default currency on login is **NGN** for every user.
 *   - Within a session manual switches (NGN ↔ GBP ↔ USD) are honoured.
 *   - On the next login the currency is forced back to NGN — by listening
 *     for a `rex:login` window event dispatched from Login.js / MFA verify.
 *
 * Region default remains "All" (cross-region view).
 */
const WorkspaceContext = createContext(null);

const CURRENCY_KEY = "rex.currency";
const REGION_KEY = "rex.region";

export const CURRENCIES = ["NGN", "GBP", "USD", "All"];
export const REGIONS = ["NG", "UK", "RoA", "All"];

const DEFAULT_CURRENCY = "NGN";
const DEFAULT_REGION = "All";

export function WorkspaceProvider({ children }) {
  const [currency, setCurrencyState] = useState(() => {
    try { return localStorage.getItem(CURRENCY_KEY) || DEFAULT_CURRENCY; }
    catch (_e) { return DEFAULT_CURRENCY; }
  });
  const [region, setRegionState] = useState(() => {
    try { return localStorage.getItem(REGION_KEY) || DEFAULT_REGION; }
    catch (_e) { return DEFAULT_REGION; }
  });

  useEffect(() => {
    try { localStorage.setItem(CURRENCY_KEY, currency); } catch (_e) {}
  }, [currency]);
  useEffect(() => {
    try { localStorage.setItem(REGION_KEY, region); } catch (_e) {}
  }, [region]);

  // REX-001: reset currency to NGN on every successful login.
  useEffect(() => {
    function onLogin() {
      setCurrencyState(DEFAULT_CURRENCY);
      try { localStorage.setItem(CURRENCY_KEY, DEFAULT_CURRENCY); } catch (_e) {}
    }
    window.addEventListener("rex:login", onLogin);
    return () => window.removeEventListener("rex:login", onLogin);
  }, []);

  const value = {
    currency,
    region,
    setCurrency: setCurrencyState,
    setRegion: setRegionState,
  };
  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    // Avoid throwing in places where the provider isn't wrapped yet
    return { currency: DEFAULT_CURRENCY, region: DEFAULT_REGION, setCurrency: () => {}, setRegion: () => {} };
  }
  return ctx;
}
